import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { cloneDeep } from "lodash";
import styles from './EditSchool.module.scss';
import Card from './card/Card';
import { Program, SelectOption } from '@types';
import ArrowDownIcon from '@assets/icons/arrows_leftright.svg?react';
import ArrowLeftIcon from '@assets/icons/arrow_back.svg?react';
import { useMediaQuery } from 'react-responsive';
import Table, { BodyColumn } from 'src/components/table/Table';
import Pagination from 'src/components/pagination/Pagination';
import { useClientPagination } from '@hooks/useClientPagination';
import FilterLayout from './filter/FilterLayout';
import Button from 'src/components/button/Button';
import { useGetAllProgramsQuery, SchoolDataFragment, useGetSchoolCoursesQuery, useCustomizeCoursesForSchoolMutation } from '@api/graphql-codegen'
import Spinner from '@components/spinner/Spinner';
import { useToast } from '@src/hooks/useToast';

type Props = {
  courses: Program[] | undefined;
};

type Sorting = {
  direction: 'asc' | 'desc';
};

type SchoolCourse = {
  id: string
  newId?: string | null
  customized: boolean
  isCustomizing: boolean
  isAusCourse: boolean
}

const perPage = 10;

function filterCourses(search?: string, country?: string) {
  if (search) {
    const s = search.toLowerCase();
    return (c: Program) => {
      if (c.name.toLowerCase().includes(s)) return true;
      return false;
    };
  }
  if (country) {
    return (c: Program) => {
      if (country === 'All' || c.country?.includes(country)) return true;
      return false;
    }
  }

  return (c: Program) => true
}

function Courses(props: Props) {
  const history = useHistory();
  const isMobile = useMediaQuery({ maxWidth: 915 });
  const [customizeCoursesForSchool, { loading: isSchoolCourseUpdating }] = useCustomizeCoursesForSchoolMutation();
  const { successToast } = useToast();

  const { state: { school: schoolData, isEdit } } = useLocation();
  const { data: allCoursesData, loading: allCoursesLoading } = useGetAllProgramsQuery();
  const { data: schoolCoursesData, loading: schoolCoursesLoading } = useGetSchoolCoursesQuery({
    variables: { input: { id: schoolData?.id } },
    skip: !schoolData?.id || !isEdit,
    fetchPolicy: 'network-only'
  });

  const [sorting, setSorting] = useState<Sorting>({direction: 'asc'});
  const [keyword, setKeyword] = useState('');
  const [country, setCountry] = useState<string>('All');
  
  const [schoolCourses, setSchoolCourses] = useState<SchoolCourse[]>([])
  const [allCourses, setAllCourses] = useState<Program[]>([]);

  useEffect(() => {
    if (allCoursesData && schoolCoursesData) {
      const _schoolCourses: SchoolCourse[] = []
      const _allCourses = (allCoursesData.programs as Program[]).map(ac => {
        const schoolCourse = (schoolCoursesData.getSchoolCourses?.programIds || []).find(sc => sc.id === ac.id)
        const isAusCourse = ac.country === 'Australia'
        if (schoolCourse) {
          _schoolCourses.push({
            id: schoolCourse.id,
            newId: schoolCourse.newId,
            customized: !!schoolCourse.customized,
            isCustomizing: false,
            isAusCourse
          })
          return { ...ac, customized: !!schoolCourse.customized }
        }
        return ac
      })

      setAllCourses(_allCourses)
      setSchoolCourses(_schoolCourses)
    }
  }, [allCoursesData, schoolCoursesData])

  const [courseIdsForSchool, customizingCourseIdsForSchool] = useMemo(() => {
    const _courseIdsForSchool: string[] = []
    const _customizingCourseIdsForSchool: string[] = []
    schoolCourses.forEach(sc => {
      _courseIdsForSchool.push(sc.id)
      if (sc.isCustomizing) {
        _customizingCourseIdsForSchool.push(sc.id)
      }
    })
    return [_courseIdsForSchool, _customizingCourseIdsForSchool]
  }, [schoolCourses])

  const filteredCourses = useMemo(() => {
    const { direction } = sorting;
    const programs = [...allCourses];
    return programs.sort((a, b) => {
      if (a.favorite && !b.favorite) return -1;
      if (!a.favorite && b.favorite) return 1;
      const aOrder = a.order ?? 1000;
      const bOrder = b.order ?? 1000;
      return direction === 'asc' ? aOrder - bOrder : bOrder - aOrder;
    });
  }, [allCourses, sorting]);

  const filter = useCallback((arr: any) => {
    return arr.filter(filterCourses(keyword, country));
  }, [keyword, country]);

  const { dataSlice, totalFilteredItems, currentPage, onChangePage } = useClientPagination({
    data: filteredCourses,
    perPage,
    onFilter: filter,
  });

  const handleChangePage = (page: number) => {
    onChangePage(page);
  };

  const handleChangeSorting = useCallback(() => () => {
    if (sorting?.direction === 'asc') {
      return setSorting({ direction: 'desc' });
    } else {
      return setSorting({ direction: 'asc' });
    }
  }, [sorting?.direction]);

  const handleChangeSearch = useCallback((v) => {
    setKeyword(v);
  }, []);

  const handleResetFilters = useCallback(() => {
    setCountry('All');
    setKeyword('');
  }, []);

  const handleChangeCountry = useCallback((option: SelectOption) => {
    setCountry(option.value);
  },[])

  const handleNextStep = useCallback(async () => {
    // if (!schoolCourses.length) return

    if (customizingCourseIdsForSchool.length > 0) {
      history.push({ pathname: '/super/school/edit/courses', state: { schoolData, schoolCourses  } })
    } else {
      const { data } = await customizeCoursesForSchool({
        variables: {
          input: {
            schoolId: schoolData.id,
            defaultPrograms: schoolCourses ? schoolCourses.map(sc => ({ id: sc.id, newId: sc.newId, isCustomizing: sc.isCustomizing })) : [],
            customizedPrograms: []
          }
        }
      })
      if (data?.customizeCoursesForSchool) {
        successToast(`${schoolData.name} is successfully updated!`);
        history.goBack()
      }
    }
  }, [customizeCoursesForSchool, customizingCourseIdsForSchool.length, history, schoolCourses, schoolData, successToast])

  const handleRowClick = useCallback((program: Program) => (name: string, value: boolean) => {
    const cloneSchoolCourses = cloneDeep(schoolCourses)
    const isAusCourse = program.country === 'Australia'

    const index = cloneSchoolCourses.findIndex(sc => sc.id === program.id)
    if (index < 0 && !!value) {
      cloneSchoolCourses.push({
        id: program.id,
        customized: false,
        isCustomizing: name === 'customize',
        isAusCourse,
      })
    } else {
      if (value) {
        cloneSchoolCourses[index] = {
          ...cloneSchoolCourses[index],
          isCustomizing: name === 'customize',
          isAusCourse
        }
      } else {
        if (name === 'customize') {
          cloneSchoolCourses[index] = {
            ...cloneSchoolCourses[index],
            isCustomizing: false,
            isAusCourse
          }
        } else {
          cloneSchoolCourses.splice(index, 1)
        }
      }
    }
    setSchoolCourses(cloneSchoolCourses)
  }, [schoolCourses]);

  const bodyColumns: BodyColumn[] = useMemo(() => {
    return [
      {
        key: (row: Program) => {
          return (
            <Card
              key={row.id}
              program={row}
              flag={row.country || ""}
              access={courseIdsForSchool.includes(row.id)}
              customize={customizingCourseIdsForSchool.includes(row.id)}
              onChange={handleRowClick(row)}
            />
          );
        }
      }
    ]
  }, [courseIdsForSchool, customizingCourseIdsForSchool, handleRowClick]);

  return (
    <div className={styles.programs}>
      <div className={styles.topBanner}>
        <div className={styles.container}>
          <ArrowLeftIcon className={styles.backIcon} fill='white' role='button' onClick={() => history.goBack()} />
          <div className={styles.title}>
            {`Check all the Units that you want ${schoolData?.name || 'School'} to have access to:`}
          </div>
        </div>
      </div>
      <div className={styles.container}>
        <FilterLayout
          keyword={keyword}
          country={country}
          handleChangeSearch={handleChangeSearch}
          handleChangeCountry={handleChangeCountry}
          handleResetFilters={handleResetFilters}
          searchPlaceHolder="Search Units..."
        />
        {isMobile ? '' : (
          <div className={styles.header}>
            <div></div>
            <div className={styles.headerFilter}>
              <div onClick={handleChangeSorting()} className={styles.filter}>
                <div className={styles.headerTitle}>Unit</div>
                <ArrowDownIcon className={`${styles.filterIcon} ${sorting?.direction === 'desc' ? styles.filterIconUp : ''}`}/>
              </div>
              <div className={styles.filter}>
                <div className={styles.headerTitle}>Country</div>
              </div>
              <div className={styles.filter}>
                <div className={styles.headerTitle}>Access</div>
              </div>
              <div className={styles.filter}>
                <div className={styles.headerTitle}>Customized</div>
              </div>
              <div className={styles.filter}>
                <div className={styles.headerTitle}>Customize</div>
              </div>
            </div>
          </div>
        )}
        <Table color={'green'} header={[]} body={bodyColumns} data={dataSlice} isLoading={allCoursesLoading || schoolCoursesLoading} />
        {!allCoursesLoading && !schoolCoursesLoading && (
          <>
            <div className={styles.paginationWrapper}>
              <Pagination currentPage={currentPage} perPage={perPage} totalItems={totalFilteredItems} onPageChange={handleChangePage}/>
            </div>
            <div className={styles.rightHeader}>
              <Button
                onClick={handleNextStep}
                className={styles.addBtn}
                disabled={isSchoolCourseUpdating || schoolCoursesLoading || allCoursesLoading}
                leftIcon={isSchoolCourseUpdating ? <Spinner width={20} height={20} /> : undefined}
                label={customizingCourseIdsForSchool.length > 0 ? 'Next' : 'Update'}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Courses;
